<script setup lang="ts">
import { breakpointsTailwind } from '@vueuse/core'
import TableRowDynamic from '../TableRowDynamic/index.vue'
import GroupedActions from '../components/GroupedActions/index.vue'
import IconBox from '~~/src/components/DrawerSidebarRight/components/shared/IconBox.vue'
import EditElemental from '~~/src/components/DrawerSidebarRight/components/shared/EditElemental/index.vue'
import { useElementalListContext } from '~~/src/components/DrawerSidebarRight/components/shared/ElementalList/ElementalListContext'
import CopyButton from '@/components/DrawerSidebarRight/components/shared/CopyButton/index.vue'
import DraggableElement from '~~/src/components/utils/DragAndDrop/DraggableElement.vue'
import { ElementalTypeEnum } from '~~/src/types/elemental'
import type { Elemental } from '~~/src/types/elemental'
import { useSidebarStore } from '~~/src/stores/sidebar'
import { useElementalListsStore } from '~~/src/stores/useElementalListsStore'
import { Button } from '~~/src/components/ui/button'
import { useTouch } from '~~/src/composables/useTouch'
import ElementalMenu from '~~/src/components/Elemental/Options/index.vue'
import ElemetalIconPicker from '~~/src/components/Elemental/ElemetalIconPicker/index.vue'
import { getElementalType } from '~~/src/utils/elementals'
import { useElementalListFilter } from '~~/src/composables/useFilteredElementals'
import Share from '@/components/Elemental/Actions/Share/index.vue'
import ElementProvider from '~~/src/components/Elemental/context/ElementProvider.vue'
import { useElementalFormStore } from '~~/src/stores/elementalForm'

const props = defineProps<{
  elemental: Elemental
  dataIndex?: number
  elementalLabel?: string
  folderLevel?: number
  columns: string[]
  rowClass?: string
}>()

const user = useUser()

const { isSmaller } = useBreakpoints(breakpointsTailwind)
const { toggleSidebar, goToFolder } = useSidebarStore()
const { listId: parentListId } = useElementalListContext()
const { open: openElementalForm } = useElementalFormStore()
const { favorite, isPending } = useFavorite(props.elemental)
const isFavorite = ref(props.elemental.isFavorite ?? false)

const router = useRouter()
const isOwner = computed(() => user.value?.id === props.elemental.user.id)
const elementalRoute = computed(() => {
  const route = getElementalTypeRoute(props.elemental.elementalTypeId)

  if (isOwner.value)
    return (`/${route}/${props.elemental.id}/edit`)
  else
    return (`/${route}/${props.elemental.url}`)
})

const onMobile = isSmaller('sm')
const goToEditSidebar = () => {
  openElementalForm('edit', unref(props.elemental), getElementalType(props.elemental.elementalTypeId, props.elemental.isList))
}

const handleFavorite = () => {
  favorite(isFavorite.value)
  isFavorite.value = !isFavorite.value
}

const handlerClick = () => {
  if (!isOwner.value) {
    router.push(elementalRoute.value)
    return
  }

  if (props.elemental.isList) {
    goToFolder(props.elemental)
    return
  }

  if (onMobile)
    toggleSidebar(false)

  goToEditSidebar()
}

const expandElemental = () => {
  toggleSidebar(false)
  setTimeout(() => {
    router.push(elementalRoute.value)
  }, 200)
}

const avatarSize = 28

const optimizedAvatar = computed(() => {
  if (!props.elemental.avatar)
    return

  return optimizeImage(props.elemental.avatar, {
    width: avatarSize * 2, height: avatarSize * 2,
  })
})
const { state, countList } = useElementalListsStore()

const { showList } = useElementalListFilter(
  computed(() => {
    return {
      list: props.elemental,
    }
  }),
  computed(() => state.query),
  computed(() => [state.filters].map(filter => ({
    id: filter.key,
    value: filter.value,
  })) ?? []),
)
const isElementalMenuHover = ref(false)
const elemetalTypeId = computed(() => getElementalType(props.elemental.elementalTypeId, props.elemental.isList))

const { onTouchEnd, onTouchMove, onTouchStart } = useTouch({ onTap: handlerClick })

const wrapperRef = ref<HTMLElement | null>(null)

const { isDragHover } = useDragHover({
  node: wrapperRef,
  disabled: !props.elemental.isList,
})

const toggleMenu = () => {
  isElementalMenuHover.value = !isElementalMenuHover.value
}

watch(() => props.elemental.isFavorite, (newValue) => {
  isFavorite.value = newValue ?? false
})
</script>

<template>
  <ElementProvider :elemental="elemental" :type="elemental.elementalTypeId">
    <DraggableElement
      is="li"
      v-show="showList"
      class="list-none border-b border-grayLighter drag-item"
      :data="{
        listId: parentListId,
        elemental,
      }"
      :text-content="elemental.template ?? elemental.title" :data-index="dataIndex"
    >
      <button
        ref="wrapperRef" class="w-full drag-image group"
        :class="[$style.wrapper, $style['drag-view'], { [$style.isDragHover]: isDragHover }]"
        @click="handlerClick" @touchstart="onTouchStart" @touchmove="onTouchMove" @touchend="onTouchEnd"
      >
        <TableRowDynamic
          :list="elemental"
          :folder-level="folderLevel ?? 1"
          :columns="columns"
          :elemental-label="elementalLabel ?? ''"
          class="w-full"
          :class="[`level-${folderLevel ?? 1}`, rowClass]"
        >
          <template #list-icon>
            <div v-if="optimizedAvatar" class="avatar h-7 w-7 flex-shrink-0">
              <div class="rounded-1 overflow-hidden">
                <img
                  :src="optimizedAvatar" :width="avatarSize" :title="`${elemental.title} avatar`"
                  :alt="`${elemental.title} avatar`" :height="avatarSize"
                >
              </div>
            </div>
            <IconBox v-else class="h-7 w-7 flex-shrink-0 !p-0">
              <ElemetalIconPicker v-if="elemetalTypeId !== 0" class="h-7 w-7 flex-shrink-0" :class="[elemetalTypeId === ElementalTypeEnum.Image ? 'h-3 w-3' : '']" :type="elemetalTypeId" />
              <ElementalElemetalFolderIconPicker v-else :type="elemetalTypeId" />
            </IconBox>
          </template>
        </TableRowDynamic>

        <div :class="$style.actions" @click.stop>
          <GroupedActions
            :is-hover="isElementalMenuHover"
            class="group-hover:opacity-100 py-1"
          >
            <template #actions>
              <ElementProvider
                :elemental="elemental"
                :type="elemental.elementalTypeId"
                :is-owner="isOwner"
              >
                <Share class="!w-8 !h-8" icon-class="!w-4 !h-4" btn-variant="snack-icon-cube-rounded" />
              </ElementProvider>
              <Button
                variant="snack-icon-cube-rounded"
                size="icon"
                icon="sm"
                class=" text-soft-black font-700 text-xs w-8 h-8"
                @click="handleFavorite"
              >
                <div v-if="isFavorite" i-mdi:star class="w-4 h-4" />
                <div v-else i-mdi:star-outline class="w-4 h-4" />
              </Button>
              <CopyButton
                :is="Button"
                v-if="elemetalTypeId !== ElementalTypeEnum.Table"
                :elemental="elemental"
                variant="snack-icon-cube-rounded"
                size="icon"
                icon-class="w-4 h-4"
                icon="sm"
                class=" text-soft-black font-700 text-xs [&_svg]:w-4 [&_svg]:h-4 w-8 h-8"
                :content="elemental.template || ''"
              />
              <EditElemental v-if="isOwner" icon-class="!w-4 !h-4" class="w-8 h-8" :elemental="elemental" btn-variant="snack-icon-cube-rounded" />
              <Button
                v-if="isOwner"
                variant="snack-icon-cube-rounded"
                size="icon"
                icon="sm"
                class=" text-soft-black font-700 text-xs w-8 h-8"
                @click="expandElemental"
              >
                <IconArrowExpand class="w-4 h-4" />
              </Button>
              <ElementalMenu
                :elemental="elemental"
                :hidde-copy-link-action="elemetalTypeId === ElementalTypeEnum.Table"
                :parent-list-id="parentListId"
                btn-class="w-8 h-8 [&_svg]:w-4 [&_svg]:h-4"
                btn-variant="snack-icon-cube-rounded"
                @open-menu="toggleMenu"
              >
                <!-- slot trigger -->
                <template #trigger="{ toggle }">
                  <Button
                    variant="snack-icon-cube-rounded"
                    size="icon"
                    icon="sm"
                    class=" text-soft-black font-700 text-xs w-8 h-8 [&_svg]:w-4 [&_svg]:h-4"
                    @click="toggle()"
                  >
                    <div class="i-material-symbols:more-vert !w-4 !h-4" />
                  </Button>
                </template>
              </ElementalMenu>
            </template>
          </GroupedActions>
          <slot name="aditional-actions" />
        </div>
      </button>
    </DraggableElement>
  </ElementProvider>
</template>

<style module lang="scss">
:deep(.drag-item) {
  &.dragged {
    opacity: 0.5;
  }
}
.drag-view {
  &[data-dragged='true'] {
    display: flex;
    align-items: center;
    @apply bg-white opacity-1 p-1.5 rounded-2; ;
  }
}

.action_button_sm {
  @apply text-soft-black font-700 text-xs w-8 h-8 group-hover;
}

.wrapper {
  @apply w-auto
  w-full
  flex
  flex-nowrap
  justify-left
  gap-2
  items-center
  relative;

  .isDragHover {
    @apply bg-grayLighter;
  }

  &:hover {
  .actions {
    .actions__hidden {
      @apply opacity-100;
      }
    }
  }

  .actions {
    @apply flex gap-1 items-center bg-transparent px-1;

    position: absolute;
    right: 0;

    .actions__hidden {
      @apply opacity-0 items-center transition-opacity duration-300;
      background-color: #fff;
      border-radius: 8px;
      border: 1px solid #E3E9F8;
      box-shadow: 0px 0px 6px 0px rgba(0, 0, 0, 0.05);
    }
  }
}
</style>
